<template>
  <div class="side-menu">
    <section class="left_wrapper" :class="[logoStyleInfo?.position]">
      <div class="logo">
        <router-link :to="`/${serviceCode}`">
          <img
            v-if="logoImagePath"
            :src="logoImagePath"
            alt="logo"
            :style="`width: ${logoStyleInfo?.width}`"
          />
        </router-link>
      </div>
      <div class="info">
        <p>{{ festaPeriod }}</p>
        <p>{{ festaStation?.name }}</p>
        <TheSNSIcons :snsURL="snsURL" />
      </div>
      <div class="dscr">
        <p v-html="entranceText"></p>
        <img
          v-if="wizdomeLogoPath"
          class="wizdome"
          :src="wizdomeLogoPath"
          alt="wizdome-logo"
        />
        <div class="sponsor" v-if="sponsorStyleInfo?.imagePath">
          <img
            :src="sponsorStyleInfo?.imagePath"
            alt="sponsor-image"
            :style="`width: ${sponsorStyleInfo?.width}`"
          />
        </div>
      </div>
    </section>
    <section class="right_wrapper" :class="layout && 'layout'">
      <div class="right_inner">
        <router-view
          v-if="isReadyToShowView"
          @update-layout="setLayout"
          @openMapInfo="openMapInfo"
          @openBusSeat="openBusSeat"
          @openCalendar="openCalendar"
          v-slot="{ Component }"
        >
          <keep-alive :include="keepComponents">
            <component
              :is="Component"
              :key="$route.name"
              ref="childComponent"
            />
          </keep-alive>
        </router-view>
      </div>
      <!-- 팝업 -->
      <BasePopup ref="popup" />
      <!-- 에러 팝업 -->
      <BaseErrorPopup ref="errorPopup" />
      <!-- 토스트 팝업 -->
      <BaseToast ref="toastPopup" />
      <!-- 정류장 지도 -->
      <MapInfo
        v-if="showMapInfo"
        @closeMapInfo="showMapInfo = false"
        :stationList="stationList"
        :nowStationIndex="nowStationIndex"
      />
      <!-- 좌석 -->
      <BookInfoBusSeat
        v-if="showBusSeat"
        @closeBusSeat="closeBusSeat"
        :allocList="allocs"
        :lineInfo="lineInfo"
        :dateIdx="dateIdx"
      />
      <!-- 달력 -->
      <BaseCalendar
        v-if="showCalendar"
        @closeCalendar="showCalendar = false"
        @onSelectDate="onSelectDate"
        :selectedIdx="selectedIdx"
        :selectedDateList="selectedDateList"
        :calendarList="calendarList"
        :selectedDate="selectedDate"
      />
    </section>
  </div>
</template>

<script>
import {
  ref,
  computed,
  shallowRef,
  getCurrentInstance,
  onMounted,
  onBeforeMount,
} from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import BasePopup from "@/components/Base/BasePopup.vue";
import BaseToast from "@/components/Base/BaseToast.vue";
import MapInfo from "@/components/Map/MapInfo.vue";
import BookInfoBusSeat from "@/components/BookInfo/BookInfoBusSeat.vue";
import BaseCalendar from "@/components/Base/BaseCalendar.vue";
import useTheme from "@/composables/useTheme";
import InlineSvg from "vue-inline-svg";
import { serviceMappers } from "@/model/mappers.js";
import TheSNSIcons from "@/components/Base/TheSNSIcons.vue";
import BaseErrorPopup from "@/components/Base/BaseErrorPopup.vue";

export default {
  name: "SideMenu",

  components: {
    BasePopup,
    BaseToast,
    MapInfo,
    BookInfoBusSeat,
    InlineSvg,
    BaseCalendar,
    TheSNSIcons,
    BaseErrorPopup,
  },
  props: {
    keepComponents: {
      type: Array,
      default: () => [],
    },
  },
  setup() {
    const { proxy } = getCurrentInstance();

    const route = useRoute();
    const store = useStore();
    const serviceCode = route.params.serviceCode;
    const entranceText = computed(() => {
      /**
       * entranceText 값 문자열에서 배열로 타입이 수정됨
       * 현재 운영중인 서비스는 텍스트로 들어올 수 있어 배열 또는 텍스트로 들어오는 경우를 모두 고려해야 함
       */
      const entranceText = store.getters["common/entranceText"];
      if (Array.isArray(entranceText)) {
        return entranceText.join("<br />");
      }

      return store.getters["common/entranceText"];
    });

    const layout = shallowRef(false);
    const setLayout = (layoutValue) => {
      layout.value = layoutValue;
    };

    const serviceStyle = ref({});
    const snsURL = ref({
      homePage: "",
      instagram: "",
      facebook: "",
    });
    const wizdomeLogoPath = ref();
    const logoStyleInfo = computed(() => serviceStyle.value?.logo);
    const logoImagePath = computed(
      () => logoStyleInfo.value?.imagePath ?? serviceStyle.value.logoPath
    );
    const sponsorStyleInfo = computed(() => serviceStyle.value?.sponsor);

    const getServiceInfo = async () => {
      if (Object.keys(serviceInfo.value).length === 0) {
        const result = await store.dispatch("common/FETCH_SERVICE_INFO", {
          serviceCode: serviceCode.toUpperCase(),
        });
        if (result.resultCode !== 0) {
          proxy.$root.$openErrorPopup({
            errorCode: result.resultCode,
            errorTime: result.resultTs,
            errorMessage: result.resultMessage,
          });
          return;
        }
      }
      serviceStyle.value =
        serviceInfo.value?.contentProps?.style ||
        serviceMappers[serviceCode.toUpperCase()];
      if (!serviceStyle.value) {
        return;
      }
      useTheme(serviceStyle.value);
      snsURL.value = serviceStyle.value.snsURL;
      wizdomeLogoPath.value = serviceStyle.value?.wizdomeLogo?.path
        ? serviceStyle.value?.wizdomeLogo?.path
        : require("@/assets/img/wizdome.png");
      if (serviceInfo.value?.targetStationUid) {
        getFestaStation();
      }
    };
    const serviceInfo = computed(() => store.state.common.serviceInfo);
    const festaPeriod = computed(() => store.getters["common/festaPeriod"]);
    const festaStation = ref({});
    const getFestaStation = async () => {
      festaStation.value = await store.dispatch("line/FETCH_STATION_POSITION", {
        stationUid: serviceInfo.value.targetStationUid,
      });
    };

    onBeforeMount(() => {
      getServiceInfo();
    });

    const showMapInfo = ref(false);
    const stationList = ref([]);
    const nowStationIndex = ref(0);
    const openMapInfo = (stations, nowStationOrder = 1) => {
      showMapInfo.value = true;
      stationList.value = stations;
      nowStationIndex.value = nowStationOrder - 1;
    };
    const showBusSeat = ref(false);
    const allocs = ref([]);
    const lineInfo = ref([]);
    const dateIdx = ref(0);
    const openBusSeat = (event) => {
      const { allocList, selectedLine, dateIndex } = event;
      showBusSeat.value = true;
      allocs.value = allocList;
      lineInfo.value = selectedLine;
      dateIdx.value = dateIndex;
    };
    const childComponent = ref(null);
    const closeBusSeat = (data) => {
      showBusSeat.value = false;
      if (data) {
        // setSeats 함수명 bookInfo 컴포넌트 이외 사용금지!
        childComponent.value.setSeats(data);
      }
    };
    const showCalendar = ref(false);
    const selectedIdx = ref(0);
    const selectedDate = ref("");
    const calendarList = ref([]);
    const selectedDateList = ref([]);
    const openCalendar = ({ idx, date, selectedDates, calendars }) => {
      showCalendar.value = true;
      selectedIdx.value = idx;
      selectedDate.value = date;
      selectedDateList.value = selectedDates;
      calendarList.value = calendars;
    };
    const onSelectDate = (date) => {
      showCalendar.value = false;
      selectedDate.value = date;
      if (selectedDate.value) {
        childComponent.value.setDate({
          idx: selectedIdx.value,
          date: selectedDate.value,
        });
      }
    };

    const isReadyToShowView = ref(false);

    const popup = ref(null);
    const errorPopup = ref(null);
    const toastPopup = ref(null);
    onMounted(() => {
      proxy.$root.$openPopup = popup.value.openPopup;
      proxy.$root.$closePopup = popup.value.closePopup;
      proxy.$root.$openErrorPopup = errorPopup.value.openErrorPopup;
      proxy.$root.$closeErrorPopup = errorPopup.value.closeErrorPopup;
      proxy.$root.$showToast = toastPopup.value.showToast;
      isReadyToShowView.value = true;
    });

    return {
      serviceStyle,
      serviceCode,
      layout,
      festaStation,
      festaPeriod,
      showMapInfo,
      stationList,
      nowStationIndex,
      showBusSeat,
      allocs,
      lineInfo,
      dateIdx,
      childComponent,
      popup,
      errorPopup,
      toastPopup,
      setLayout,
      openMapInfo,
      openBusSeat,
      closeBusSeat,
      snsURL,
      openCalendar,
      showCalendar,
      onSelectDate,
      selectedIdx,
      selectedDate,
      selectedDateList,
      calendarList,
      entranceText,
      wizdomeLogoPath,
      logoStyleInfo,
      logoImagePath,
      sponsorStyleInfo,
      isReadyToShowView,
    };
  },
};
</script>
